import type { MotionPropsFor } from '@hover/blueprint';
import {
  Box,
  HStack,
  IconButton,
  Motion,
  VStack,
  motion,
  system,
  useDisclosure,
} from '@hover/blueprint';
import { zIndices } from '@hover/blueprint/foundation';
import {
  iconAddStructure,
  iconPlus,
  iconUploadPhoto,
  iconUser,
  iconUserFilled,
  iconViewGridMedium,
} from '@hover/icons';
import { AnimatePresence, type Variants } from 'framer-motion';
import React from 'react';

import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import type { TileButtonProps } from '../TileButton';
import { TileButton } from '../TileButton';

const MotionDiv = motion(system.div);
const MotionBox = motion(Box);
const MotionIconButton = motion(IconButton);
const MotionTileButton = motion(TileButton);

const iconVariants: Variants = {
  hidden: { opacity: 0, transition: { duration: 0.2 } },
  visible: { opacity: 1, transition: { duration: 0.2 } },
};

export const FLOATING_NAV_HEIGHT = 64;

const menuVariants = {
  closed: { height: `${FLOATING_NAV_HEIGHT}px`, width: '178px' },
  open: { height: '200px', width: '245px', borderRadius: '24px' },
};

const optionVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

interface DimmingOverlayProps extends MotionPropsFor<typeof MotionDiv> {
  isDimmed: boolean;
  onClick: () => void;
}

const DimmingOverlay = ({ isDimmed = false, onClick }: DimmingOverlayProps) => (
  <MotionDiv
    animate={isDimmed ? 'visible' : 'hidden'}
    backgroundColor="overlayDark"
    height="100vh"
    initial="hidden"
    left={0}
    onClick={onClick}
    pointerEvents={isDimmed ? 'auto' : 'none'}
    position="fixed"
    top={0}
    transition={{ duration: 0.2 }}
    variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
    width="100vw"
    zIndex="overlay"
  />
);

interface MenuOptionProps extends MotionPropsFor<TileButtonProps> {
  delay?: number;
}

const MenuOption = ({ delay, label, ...rest }: MenuOptionProps) => (
  <MotionTileButton
    animate="visible"
    colorScheme="light"
    exit="hidden"
    height={525}
    iconSize={200}
    initial="hidden"
    label={label}
    marginLeft="0 !important"
    textSize={300}
    transition={{ duration: 0.2, delay }}
    variants={optionVariants}
    width={525}
    {...rest}
  />
);

interface FloatingNavProps {
  onHomeClick?: () => void;
  onProfileClick?: () => void;
  isProfileFilled?: boolean;
  onAddProjectClick?: () => void;
  onAddPhotoClick?: () => void;
  defaultIsOpen?: boolean;
  showNavbar?: boolean;
  isNavbarDimmed?: boolean;
}

const FloatingNav = ({
  onHomeClick = () => null,
  onProfileClick = () => null,
  isProfileFilled = false,
  onAddProjectClick = () => null,
  onAddPhotoClick = () => null,
  defaultIsOpen = false,
  showNavbar = true,
  isNavbarDimmed = false,
}: FloatingNavProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { isOpen, onClose, onOpen } = useDisclosure({
    defaultIsOpen,
  });

  const options = [
    {
      icon: iconAddStructure,
      label: t('floatingNavbar.project'),
      onClick: () => {
        onAddProjectClick();
        onClose();
      },
    },
    {
      icon: iconUploadPhoto,
      label: t('floatingNavbar.photo'),
      onClick: () => {
        onAddPhotoClick();
        onClose();
      },
    },
  ];

  const navbarZIndex = isNavbarDimmed ? zIndices.overlay - 1 : zIndices.modal;
  return (
    <Motion.Firm>
      <DimmingOverlay isDimmed={isOpen} onClick={onClose} />
      <MotionBox
        animate={isOpen ? 'open' : 'closed'}
        backgroundColor="neutral.0"
        borderColor="neutral.100"
        borderRadius="32px"
        borderStyle="solid"
        borderWidth="1px"
        bottom={{ base: '18px', tablet: 600 }}
        data-testid="floating-nav"
        display={showNavbar ? 'block' : 'none'}
        initial="closed"
        layout
        padding="18px"
        position="fixed"
        shadow="default"
        transition={{ duration: 0.2 }}
        variants={menuVariants}
        zIndex={navbarZIndex}
      >
        <AnimatePresence>
          <VStack justifyContent="space-between" width="100%">
            {isOpen && (
              <HStack
                justifyContent="space-evenly"
                marginTop={300}
                width="100%"
              >
                {options.map(({ icon, label, onClick }, index) => (
                  <MenuOption
                    delay={0.1 + 0.05 * index}
                    icon={icon}
                    key={label}
                    label={label}
                    onClick={onClick}
                  />
                ))}
              </HStack>
            )}
            <HStack
              bottom={0}
              height={300}
              justifyContent="space-evenly"
              marginX={300}
              paddingBottom={400}
              position="absolute"
              width="155px"
            >
              {!isOpen && (
                <MotionIconButton
                  _focusVisible={{ outlineOffset: 0 }}
                  animate="visible"
                  exit="hidden"
                  icon={iconViewGridMedium}
                  initial="hidden"
                  label={t('floatingNavbar.home')}
                  marginX="0 !important"
                  onClick={onHomeClick}
                  variant="ghost"
                  variants={iconVariants}
                />
              )}
              <MotionIconButton
                _focusVisible={{ outlineOffset: 0 }}
                data-testid="floating-nav-add"
                icon={iconPlus}
                label={
                  isOpen ? t('floatingNavbar.close') : t('floatingNavbar.add')
                }
                marginX="0 !important"
                onClick={isOpen ? onClose : onOpen}
                variant="ghost"
                variants={{ open: { rotate: 45 }, closed: { rotate: 0 } }}
              />
              {!isOpen && (
                <MotionIconButton
                  _focusVisible={{ outlineOffset: 0 }}
                  animate="visible"
                  exit="hidden"
                  icon={isProfileFilled ? iconUserFilled : iconUser}
                  initial="hidden"
                  label={t('floatingNavbar.profile')}
                  marginX="0 !important"
                  onClick={onProfileClick}
                  variant="ghost"
                  variants={iconVariants}
                />
              )}
            </HStack>
          </VStack>
        </AnimatePresence>
      </MotionBox>
    </Motion.Firm>
  );
};

export { FloatingNav };
